export enum EIsoTimeUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
}

/**
 * ISO 8601 duration sign
 */
export type IsoSign = '+' | '-'

export type TISODuration = `${IsoSign}${string}`

/**
 * Parsed ISO 8601 duration
 */
export type ParsedIsoDuration = {
  value: number
  unit: EIsoTimeUnit
  sign: IsoSign
}

/**
 * Formats a duration to ISO 8601 duration format.
 *
 * Supported formats:
 * - Minutes: PT30M
 * - Hours: PT2H
 * - Days: P7D
 * - Weeks: P14D (converted to days: 1 week = 7 days)
 * - Months: P1M
 *
 * @param params - Duration parameters
 * @param params.value - The duration value
 * @param params.unit - The time unit
 * @param params.sign - Optional sign prefix ('+' or '-'). Defaults to '+'
 * @returns ISO 8601 duration string with sign prefix (e.g., '+P7D', '-PT2H')
 * @throws Error if the unit is not supported
 *
 * @example
 * formatIsoDuration({ value: 7, unit: EIsoTimeUnit.DAYS }) // '+P7D'
 * formatIsoDuration({ value: 2, unit: EIsoTimeUnit.HOURS, sign: '-' }) // '-PT2H'
 * formatIsoDuration({ value: 2, unit: EIsoTimeUnit.WEEKS }) // '+P14D' (2 weeks = 14 days)
 */
export function formatIsoDuration(params: {
  value: number
  unit: EIsoTimeUnit
  sign?: IsoSign
}): TISODuration {
  const { value, unit, sign = '+' } = params
  let duration: string

  switch (unit) {
    case EIsoTimeUnit.MINUTES:
      duration = `PT${value}M`
      break
    case EIsoTimeUnit.HOURS:
      duration = `PT${value}H`
      break
    case EIsoTimeUnit.DAYS:
      duration = `P${value}D`
      break
    case EIsoTimeUnit.WEEKS:
      duration = `P${value * 7}D` // Convert weeks to days
      break
    case EIsoTimeUnit.MONTHS:
      duration = `P${value}M`
      break
    default:
      throw new Error(`Unsupported time unit: ${unit}`)
  }

  return `${sign}${duration}`
}

/**
 * Parses an ISO 8601 duration string into its components.
 *
 * Supported formats:
 * - Minutes: PT30M → { value: 30, unit: MINUTES, sign: '+' }
 * - Hours: PT2H → { value: 2, unit: HOURS, sign: '+' }
 * - Days: P7D → { value: 7, unit: DAYS, sign: '+' }
 * - Months: P1M → { value: 1, unit: MONTHS, sign: '+' }
 *
 * Sign prefix:
 * - '+' prefix or no prefix → sign: '+'
 * - '-' prefix → sign: '-'
 *
 * Note: Weeks are never returned. All days remain as DAYS unit, even if they're multiples of 7.
 *
 * @param isoDuration - ISO 8601 duration string (e.g., '-P1D', '+PT2H', 'P3M')
 * @returns Parsed duration with value, unit, and sign
 * @throws Error if the duration format is invalid or unsupported
 *
 * @example
 * parseIsoDuration('-P1D') // { value: 1, unit: DAYS, sign: '-' }
 * parseIsoDuration('+PT2H') // { value: 2, unit: HOURS, sign: '+' }
 * parseIsoDuration('P3M') // { value: 3, unit: MONTHS, sign: '+' }
 */
export type ParsedCombinedIsoDuration = {
  days: number
  hours: number
  minutes: number
  sign: IsoSign
}

export function formatCombinedIsoDuration(params: {
  days: number
  hours: number
  minutes: number
  sign?: IsoSign
}): TISODuration {
  const { days, hours, minutes, sign = '+' } = params
  if (days === 0 && hours === 0 && minutes === 0) {
    return `${sign}PT0M`
  }
  const daysPart = days > 0 ? `${days}D` : ''
  let timePart = ''
  if (hours > 0 || minutes > 0) {
    timePart = 'T'
    if (hours > 0) timePart += `${hours}H`
    if (minutes > 0) timePart += `${minutes}M`
  }
  return `${sign}P${daysPart}${timePart}`
}

export function parseCombinedIsoDuration(isoDuration: TISODuration | string): ParsedCombinedIsoDuration {
  const sign: IsoSign = isoDuration.startsWith('-') ? '-' : '+'
  const durationWithoutSign = isoDuration.replace(/^[+-]/, '')
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(?:\d+(?:\.\d+)?)S)?)?$/.exec(durationWithoutSign)
  if (!match || durationWithoutSign === 'P' || durationWithoutSign === 'PT') {
    throw new Error(`Unsupported ISO duration format: ${isoDuration}`)
  }
  return {
    days: Number.parseInt(match[1] ?? '0', 10),
    hours: Number.parseInt(match[2] ?? '0', 10),
    minutes: Number.parseInt(match[3] ?? '0', 10),
    sign,
  }
}

export function parseIsoDuration(isoDuration: TISODuration | string): ParsedIsoDuration {
  // Extract sign
  const sign: IsoSign = isoDuration.startsWith('-') ? '-' : '+'

  // Remove sign prefix
  const durationWithoutSign = isoDuration.replace(/^[+-]/, '')

  // Regex patterns for different duration units
  const minutesMatch = /^PT(\d+)M$/.exec(durationWithoutSign)
  const hoursMatch = /^PT(\d+)H$/.exec(durationWithoutSign)
  const daysMatch = /^P(\d+)D$/.exec(durationWithoutSign)
  const monthsMatch = /^P(\d+)M$/.exec(durationWithoutSign)
  const secondsOnlyMatch = /^PT(?:\d+(?:\.\d+)?)S$/.exec(durationWithoutSign)

  if (secondsOnlyMatch) {
    return {
      value: 0,
      unit: EIsoTimeUnit.MINUTES,
      sign,
    }
  }

  if (minutesMatch) {
    return {
      value: Number.parseInt(minutesMatch[1]!, 10),
      unit: EIsoTimeUnit.MINUTES,
      sign,
    }
  }

  if (hoursMatch) {
    return {
      value: Number.parseInt(hoursMatch[1]!, 10),
      unit: EIsoTimeUnit.HOURS,
      sign,
    }
  }

  if (daysMatch) {
    return {
      value: Number.parseInt(daysMatch[1]!, 10),
      unit: EIsoTimeUnit.DAYS,
      sign,
    }
  }

  if (monthsMatch) {
    return {
      value: Number.parseInt(monthsMatch[1]!, 10),
      unit: EIsoTimeUnit.MONTHS,
      sign,
    }
  }

  throw new Error(`Unsupported ISO duration format: ${isoDuration}`)
}
